/**
 * Client-side 401 handling — stop the refetch storm, redirect to Hub login.
 *
 * ## Problem this solves
 *
 * When the `animas_auth` cookie (15-min access token) AND the 7-day refresh
 * token have BOTH expired mid-session, every social-api data endpoint
 * (`/api/client-brands`, `/api/accounts`, `/api/designs`,
 * `/api/brand-kits/active`, `/api/format-presets`, `/api/inbox`, …) returns
 * HTTP 401. The consolidated hub pages mount 6+ distinct TanStack Query
 * keys, and each `?tab=` switch remounts more — so a single expired session
 * produces a burst of 401s that hammered the API (18% of all social-api
 * requests, tripping SecurityCFAccessAuthFailureSpike +
 * SecurityBruteForceAuthFailureBurst prod alerts on 2026-07-18). The user
 * meanwhile stares at broken/empty pages because nothing navigates them to
 * re-auth.
 *
 * The shared QueryClient (`createStandardQueryClient`) already disables
 * *retry* on `AuthError` (401/403), so this is NOT a per-query retry loop —
 * it is many independent queries each firing once with no escape hatch. The
 * fix is a single, idempotent redirect: on the FIRST 401, navigate the
 * browser to the Hub login URL (which unmounts the whole app and stops every
 * in-flight and future query). Every subsequent 401 during the redirect
 * window is swallowed — no repeat navigation, no further work.
 *
 * ## Why an axios response interceptor (social-side, no shared-package edit)
 *
 * `createShell()` (from `@plerity/dashboard-shell`) creates its QueryClient
 * inside `AppShell` and exposes no `queryClient` / `onUnauthorized` seam that
 * social can pass through without editing the shared package. Both the
 * dashboard-shared `apiClient` (used by `useApiQuery`/`useApiMutation`) and
 * the app's local `client` (mutations, typed calls) are plain axios instances
 * built by `createFetcher`, so a response interceptor is the clean, correct
 * social-side seam. Navigation unmounts the app, which is sufficient to stop
 * the storm on its own; retry is already off for auth failures upstream.
 */

import type { AxiosError, AxiosInstance } from "axios";

/**
 * Module-level guard so only the FIRST 401 across ANY client triggers a
 * redirect. Shared across every axios instance the app installs the
 * interceptor on (shared apiClient + local client), so concurrent 401s from
 * six different endpoints redirect exactly once.
 */
let redirecting = false;

/**
 * Test-only reset. Vitest re-imports this module per file but not per test,
 * so the guard must be resettable between cases. Not exported from any barrel;
 * app code never calls this.
 */
export function __resetAuthRedirectForTests(): void {
  redirecting = false;
}

/** Whether a redirect is already in flight (test assertion helper). */
export function __isRedirecting(): boolean {
  return redirecting;
}

/** Pull an HTTP status off an axios error or a normalized `{ status }` error. */
function statusOf(error: unknown): number | undefined {
  if (error == null || typeof error !== "object") return undefined;
  // Axios error: error.response.status
  const response = (error as AxiosError).response;
  if (response && typeof response.status === "number") return response.status;
  // Normalized ApiError / AuthError: top-level `status`
  const status = (error as { status?: unknown }).status;
  return typeof status === "number" ? status : undefined;
}

/**
 * Redirect the browser to the Hub login page, preserving the current URL as a
 * `return_to` param so the user lands back where they were after re-auth
 * (mirrors AuthGuard's redirect contract in `@plerity/auth`). Idempotent: the
 * `redirecting` guard ensures this runs at most once per page lifetime.
 *
 * Exported so `main.tsx` can wire it and so it is unit-testable in isolation
 * (mock `window.location.assign`).
 */
export function redirectToLogin(loginUrl: string): void {
  if (redirecting) return;
  redirecting = true;
  const returnTo = encodeURIComponent(window.location.href);
  window.location.assign(`${loginUrl}?return_to=${returnTo}`);
}

/**
 * Install a response interceptor that redirects to Hub login on the first
 * HTTP 401, swallowing every subsequent 401 without re-triggering navigation.
 *
 * Runs BEFORE the app's own response handling. On a 401 it redirects (once)
 * and returns a never-resolving promise so no downstream `.catch` fires an
 * error toast / retry during the brief redirect window. Non-401 errors are
 * re-thrown unchanged so the existing error-normalisation +
 * observability-reporting pipeline (in `createFetcher`) still runs.
 *
 * @param client   axios instance (shared apiClient or the app's local client)
 * @param loginUrl absolute Hub login URL (pass `PORTAL_URLS.LOGIN`)
 */
export function installAuthRedirectInterceptor(client: AxiosInstance, loginUrl: string): void {
  client.interceptors.response.use(
    (response) => response,
    (error: unknown) => {
      if (statusOf(error) === 401) {
        redirectToLogin(loginUrl);
        // Swallow the error: the app is navigating away. Returning a
        // never-resolving promise prevents downstream error handlers /
        // toasts / React-Query error states from firing mid-redirect.
        return new Promise<never>(() => {});
      }
      return Promise.reject(error);
    }
  );
}
